import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useMediaQuery } from "@mui/material";
import Fade from "@mui/material/Fade";
import Tooltip from "@mui/material/Tooltip";
import { useIsClient } from "@uidotdev/usehooks";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import type { CSSProperties } from "react";
import { Fragment, useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useSpring } from "@react-spring/web";
import { bindActionCreators } from "redux";
import * as config from "../../../config/config";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  clearCompanyList,
  getCompanyWithoutRedux,
  updateStateRegions,
} from "../../../redux/actions/company_action";
import { getCompanyListBanner } from "../../../redux/actions/job_action";
import { store } from "../../../redux/stores/store";
import {
  ContainerStyled,
  FilterSubtitle,
  GridContainer,
  GridContainerInner,
  GridFilterContainer,
  GridFilters,
  GridHeaderContainer,
  GridLinearOverlay,
  GridLogoContainer,
  GridPlayButtonOverlay,
  GridTitle,
  IconButtonStyled,
  ImageStyled,
  ReactPlayerStyled,
  TitleStyled,
} from "./styles";

let loopCompanyNameInterval: ReturnType<typeof setInterval> | undefined;
let fetchIndustryCount = 0;
let fetchStateRegionCount = 0;
let companyNameIndex = 1;
let useCurrentCompanyName = true;

/*
 * 1) Will call graphQL to get company industries
 * 2) Will call graphQL to get state region here
 */
function CompanySearchFilters(props) {
  const isMobile = useMediaQuery("(max-width: 640px)");

  const isClient = useIsClient();

  const showFilter = useSelector((state: any) => state.navbar.showFilter);
  const [playing, setPlaying] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const [currentCompanyName, setCurrentCompanyName] =
    useState("Shopline Malaysia");
  const [discoverData, setDiscoverData] = useState(null);

  const router = useRouter();

  const FALLBACK_COMPANY_LOGO =
    "https://assets.wobbjobs.com/images/hiredly/hiredly-favicon_520x520.png";
  const FALLBACK_VIDEO_MOBILE =
    "/images/landing/featured_company_banner/jobs_malaysia_hiredly_video_banner_mobile.mp4";
  const FALLBACK_VIDEO_WEB =
    "/images/landing/featured_company_banner/jobs_malaysia_hiredly_video_banner.mp4";
  const FALLBACK_COMPANY_LINK = "https://my.hiredly.com/companies/hiredly";
  const FALLBACK_TITLE = "Discover Your Future Company";

  // Prepare data with fallbacks
  const title = discoverData?.title || FALLBACK_TITLE;
  const companyLink = discoverData?.companyLink || FALLBACK_COMPANY_LINK;
  const companyLogoUrl =
    discoverData?.companyImage?.data?.attributes?.url || FALLBACK_COMPANY_LOGO;
  const mobileVideoUrl =
    discoverData?.mobileVideo?.data?.attributes?.url ||
    `${config.assetDomain}${FALLBACK_VIDEO_MOBILE}`;
  const webVideoUrl =
    discoverData?.webVideo?.data?.attributes?.url ||
    `${config.assetDomain}${FALLBACK_VIDEO_WEB}`;

  const companyNames = [
    "Shopline Malaysia",
    "Lazada Malaysia",
    "VF Corporation",
    "Ninja Van",
    "AIA BHD",
    "Hiredly.Com",
  ];

  const fetchIndustryTries = 5;
  const fetchStateRegionTries = 5;

  useEffect(() => {
    setCompanyNameLoopInterval();
    return () => {
      clearCompanyNameLoopInterval();
    };
  }, []);

  function setCompanyNameLoopInterval() {
    if (store.getState().companies.companyFilter["keyword"]) {
      useCurrentCompanyName = false;
      return;
    }

    clearCompanyNameLoopInterval();

    // Set loop company name interval
    loopCompanyNameInterval = setInterval(loopThroughCompanyName, 5000);
  }

  function clearCompanyNameLoopInterval() {
    // Clear interval
    if (loopCompanyNameInterval) {
      clearInterval(loopCompanyNameInterval);
    }
  }

  function loopThroughCompanyName() {
    let index = companyNameIndex % 4;

    let companyName = companyNames[index];

    setCurrentCompanyName(companyName);

    companyNameIndex++;

    if (companyNameIndex > 10000000) {
      companyNameIndex = 0;
    }
  }

  function handleClick() {
    toggleVideoPlayback();
  }

  function handleVideoClick() {
    toggleVideoPlayback();
  }

  async function trackPlayPauseBanner(newPlayingState: boolean) {
    if (newPlayingState) {
      return;
    }

    let slug = "";
    if (companyLink) {
      const parts = companyLink.split("/");
      slug = parts[parts.length - 1];
    }

    let companyId = null;
    if (slug) {
      try {
        const param = { companyId: slug };
        const companyDetail = await getCompanyWithoutRedux(param);
        companyId = companyDetail?.id;
      } catch (err) {
        console.error("Error fetching company by slug:", err);
      }
    }

    sendTrackingEvent({
      event: "CE_play_pause_banner",
      "company-id": companyId,
      origin: "companies_page",
      // action: newPlayingState ? "play" : "pause",
    });
  }

  function toggleVideoPlayback() {
    const newPlayingState = !playing;
    setPlaying(newPlayingState);
    setShowControls(true);
    trackPlayPauseBanner(newPlayingState);

    // Hide controls after 2 seconds when resuming
    if (newPlayingState) {
      setTimeout(() => {
        setShowControls(false);
      }, 2000);
    }
  }

  // For auto-scroll on click Discover Now
  const playerRef = useRef(null);

  const dispatch = useDispatch();
  // const companyBanner = useSelector(
  //   (state: any) => state.companies.companyPageBanner
  // );

  // React Spring animation for title text
  const titleAnimateFirst = useSpring({
    to: { opacity: 1 },
    from: { opacity: 0 },
    delay: 700,
    config: { duration: 1000 },
  });

  function getVideoUrl() {
    if (discoverData) {
      if (isMobile) {
        return mobileVideoUrl;
      } else {
        return webVideoUrl;
      }
    }

    // Fallback to old URLs if discoverData is not available
    if (isMobile) {
      return `${config.assetDomain}${FALLBACK_VIDEO_MOBILE}`;
    } else {
      return `${config.assetDomain}${FALLBACK_VIDEO_WEB}`;
    }
  }

  const videoUrl = getVideoUrl();

  // const companyUrl = "";
  // async function getBanner() {
  //   // TODO: resolve TypeScript error
  //   // @ts-ignore
  //   dispatch(getCompanyPageBanner());
  // }

  // useEffect(() => {
  //   getBanner();
  // }, [dispatch]);

  useEffect(() => {
    const fetchCompanyListBanner = async () => {
      try {
        const result = await getCompanyListBanner();
        if (result && result.attributes) {
          const attributes = result.attributes;
          setDiscoverData(attributes);

          // Extract slug from companyLink
          const companyLink = attributes.companyLink;
          let slug = "";
          if (companyLink) {
            // Example: https://my.hiredly.com/companies/hiredly
            const parts = companyLink.split("/");
            slug = parts[parts.length - 1];
          }

          // When fetching company by slug, set param as { companyId: slug }
          if (slug) {
            try {
              const param = { companyId: slug };
              const companyDetail = await getCompanyWithoutRedux(param);
              const companyId = companyDetail?.id;
              setDiscoverData((prev) => ({ ...prev, companyId }));
            } catch (err) {
              console.error("Error fetching company by slug:", err);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching company list banner:", error);
      }
    };

    fetchCompanyListBanner();
  }, []);

  async function trackCompanyIconClick(companyLink: string) {
    // Extract slug from companyLink
    let slug = "";
    if (companyLink) {
      const parts = companyLink.split("/");
      slug = parts[parts.length - 1];
    }

    let companyId = null;
    if (slug) {
      try {
        const param = { companyId: slug };
        const companyDetail = await getCompanyWithoutRedux(param);
        companyId = companyDetail?.id;
      } catch (err) {
        console.error("Error fetching company by slug:", err);
      }
    }

    sendTrackingEvent({
      event: "CE_company_icon_click_companies_page",
      "company-id": companyId,
      origin: "companies_page",
    });
    Cookies.set("click-source", "companies_page_banner_company_icon");

    router.push(companyLink);
  }

  const ssrOnlyH1Style: CSSProperties = {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: 0,
  };

  return (
    <Fragment>
      {!isClient ? (
        <h1 style={ssrOnlyH1Style}>{title}</h1>
      ) : (
        <ContainerStyled ref={playerRef} onClick={handleVideoClick}>
          <ReactPlayerStyled
            url={videoUrl}
            playing={playing}
            loop={true}
            muted={true}
            width="100%"
            height="100%"
            style={{
              maxHeight: "700px",
              overflow: "hidden",
            }}
            playsInline={true}
            config={{
              file: {
                attributes: {
                  playsInline: true,
                  muted: true,
                  autoPlay: true,
                  loop: true,
                },
              },
            }}
          />

          {/* Permanent Linear Gradient Overlay */}
          <GridLinearOverlay />

          {/* Dark Overlay - Shows only when paused */}
          <GridFilters $isVisible={!playing}>
            <GridFilterContainer>
              <GridContainer>
                <GridContainerInner>
                  <FilterSubtitle textAlign={"center"}>
                    {/* {companyBanner.description} */}
                  </FilterSubtitle>
                </GridContainerInner>
              </GridContainer>
            </GridFilterContainer>
          </GridFilters>

          {/* Company Logo and Title - Always visible */}
          <GridHeaderContainer
            onClick={(e) => {
              e.stopPropagation();
              trackCompanyIconClick(companyLink);
            }}
          >
            <Tooltip
              title="Click to View Company Profile"
              placement={"right"}
              arrow
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 400 }}
            >
              <GridLogoContainer id={"company-logo-button"}>
                <ImageStyled src={companyLogoUrl} alt="company-logo" />
              </GridLogoContainer>
            </Tooltip>
            <GridTitle>
              <TitleStyled
                style={titleAnimateFirst}
                variant="h1"
                component="h1"
              >
                {title}
              </TitleStyled>
            </GridTitle>
          </GridHeaderContainer>

          {/* Play/Pause Button */}
          <GridPlayButtonOverlay $isVisible={showControls || !playing}>
            <IconButtonStyled
              id={"video-play-pause-button"}
              aria-label="video-play-pause"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
              // TODO: resolve TypeScript error
              // @ts-ignore
              fontSize="large"
            >
              {playing ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButtonStyled>
          </GridPlayButtonOverlay>
        </ContainerStyled>
      )}
    </Fragment>
  );
}

const mapStateToProps = (state) => {
  return {
    industries: state.companies.industries,
    companyFilter: state.companies.companyFilter,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    clearCompanyList: bindActionCreators(clearCompanyList, dispatch),
    updateStateRegions: bindActionCreators(updateStateRegions, dispatch),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompanySearchFilters);
